import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  Clock, 
  ArrowLeft,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Mail
} from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import ScrollToTop from './ScrollToTop';
import WhatsAppButton from './WhatsAppButton';
import SEOHead from './SEOHead';

import { BACKEND_URL_EXPORT as BACKEND_URL } from '../../config';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [currency, setCurrency] = useState('USD');
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const convertToHtml = (text) => {
    if (!text) return '';
    
    const html = text
      .split('\n\n')
      .map(paragraph => {
        const trimmed = paragraph.trim();
        if (!trimmed) return '';
        
        if (trimmed.startsWith('# ')) {
          return `<h1>${trimmed.slice(2)}</h1>`;
        }
        if (trimmed.startsWith('## ')) {
          return `<h2>${trimmed.slice(3)}</h2>`;
        }
        if (trimmed.startsWith('### ')) {
          return `<h3>${trimmed.slice(4)}</h3>`;
        }
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
          const items = trimmed.split('\n').map(item => {
            const content = item.replace(/^[-*]\s*/, '').trim();
            return `<li>${content}</li>`;
          }).join('');
          return `<ul>${items}</ul>`;
        }
        if (/^\d+\.\s/.test(trimmed)) {
          const items = trimmed.split('\n').map(item => {
            const content = item.replace(/^\d+\.\s*/, '').trim();
            return `<li>${content}</li>`;
          }).join('');
          return `<ol>${items}</ol>`;
        }
        
        return `<p>${trimmed.replace(/\n/g, '<br>')}</p>`;
      })
      .join('\n');
    
    return html;
  };

  // Fallback sample posts (if API is unavailable)
  const fallbackPosts = {
    'guide-to-buying-japanese-used-cars': {
      title: 'Complete Guide to Buying Japanese Used Cars',
      excerpt: 'Everything you need to know about purchasing quality used vehicles from Japan.',
      category: 'Buying Guide',
      date: '2024-12-10',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1200',
      author: 'Global Trade Car Team',
      content: `
        <p>Buying a used car from Japan can be one of the best decisions you make. Japanese vehicles are renowned worldwide for their reliability, quality, and value for money. In this comprehensive guide, we'll walk you through everything you need to know about purchasing a Japanese used car.</p>

        <h2>Why Buy from Japan?</h2>
        <p>Japan has one of the most stringent vehicle inspection systems in the world. The "Shaken" inspection ensures that vehicles are maintained to high standards. Additionally, Japanese car culture emphasizes regular maintenance and care, meaning even used vehicles are often in excellent condition.</p>

        <h3>Key Advantages:</h3>
        <ul>
          <li>Superior build quality and reliability</li>
          <li>Well-maintained vehicles with complete service history</li>
          <li>Competitive pricing compared to local markets</li>
          <li>Wide variety of models and specifications</li>
          <li>Transparent auction grading system</li>
        </ul>

        <h2>Understanding the Process</h2>
        <p>The process of buying a Japanese used car involves several key steps:</p>

        <h3>1. Vehicle Selection</h3>
        <p>Browse available inventory and use filters to narrow down your options based on make, model, year, price range, and specifications. Pay attention to auction grades which indicate the vehicle's condition.</p>

        <h3>2. Vehicle Inspection</h3>
        <p>All vehicles come with detailed inspection reports. These reports include information about the vehicle's condition, any damage, service history, and auction grade. Review these carefully before making a decision.</p>

        <h3>3. Purchase and Payment</h3>
        <p>Once you've selected a vehicle, you'll receive a proforma invoice detailing all costs including FOB price, documentation fees, and any additional charges. Payment is typically made via international bank transfer.</p>

        <h3>4. Shipping and Delivery</h3>
        <p>After payment confirmation, your vehicle will be prepared for export. This includes final inspection, export documentation, and arrangement of shipping. Shipping times vary by destination but typically range from 2-8 weeks.</p>

        <h2>What to Look For</h2>
        <p>When selecting a Japanese used car, consider these important factors:</p>

        <ul>
          <li><strong>Auction Grade:</strong> Higher grades (4.0 and above) indicate better condition</li>
          <li><strong>Mileage:</strong> Japanese domestic market vehicles often have lower mileage</li>
          <li><strong>Service History:</strong> Complete maintenance records add value</li>
          <li><strong>Accident History:</strong> Check for any repair records</li>
          <li><strong>Age:</strong> Consider import regulations in your country</li>
        </ul>

        <h2>Cost Considerations</h2>
        <p>Understanding the total cost is crucial. Your total investment includes:</p>

        <ul>
          <li>FOB (Free on Board) price - the vehicle cost at the Japanese port</li>
          <li>Freight charges - shipping to your destination</li>
          <li>Insurance - marine insurance for transit</li>
          <li>Import duties and taxes - varies by country</li>
          <li>Customs clearance fees</li>
          <li>Local registration and compliance costs</li>
        </ul>

        <h2>Common Mistakes to Avoid</h2>
        <p>First-time buyers often make these mistakes. Here's how to avoid them:</p>

        <ul>
          <li>Not researching import regulations in their country</li>
          <li>Overlooking additional costs beyond FOB price</li>
          <li>Ignoring auction grades and inspection reports</li>
          <li>Not arranging marine insurance</li>
          <li>Failing to work with reputable exporters</li>
        </ul>

        <h2>Final Thoughts</h2>
        <p>Buying a Japanese used car is a rewarding experience when done correctly. By following this guide and working with a trusted exporter like Global Trade Car, you can confidently navigate the process and find the perfect vehicle for your needs.</p>

        <p>Ready to start your journey? Browse our current inventory or contact our team for personalized assistance.</p>
      `
    },
    'understanding-japanese-auction-grades': {
      title: 'Understanding Japanese Auction Grades',
      excerpt: 'Learn how to read and understand Japanese auction grading systems.',
      category: 'Education',
      date: '2024-12-08',
      readTime: '6 min read',
      image: 'https://images.unsplash.com/photo-1486496146582-9ffcd0b2b2b7?w=1200',
      author: 'Global Trade Car Team',
      content: `
        <p>The Japanese auction grading system is one of the most reliable indicators of a used vehicle's condition. Understanding these grades is crucial for making informed purchase decisions.</p>

        <h2>What Are Auction Grades?</h2>
        <p>Japanese auction houses employ professional inspectors who thoroughly examine each vehicle and assign a grade based on its overall condition. These grades provide buyers with an objective assessment of the vehicle's quality.</p>

        <h2>The Grading Scale</h2>
        <h3>Grade S (State)</h3>
        <p>Virtually new vehicles with minimal mileage (usually under 10,000 km). These are often dealer demos or pre-registered vehicles.</p>

        <h3>Grade 6</h3>
        <p>New or near-new condition with very low mileage. Rare to find in auctions.</p>

        <h3>Grade 5</h3>
        <p>Excellent condition with minimal wear. Well-maintained with low mileage.</p>

        <h3>Grade 4.5</h3>
        <p>Very good condition. Minor imperfections may exist but overall excellent.</p>

        <h3>Grade 4</h3>
        <p>Good condition with normal wear appropriate for age and mileage. Most popular grade.</p>

        <h3>Grade 3.5</h3>
        <p>Average condition with some visible wear but mechanically sound.</p>

        <h3>Grade 3</h3>
        <p>Fair condition requiring minor repairs or cosmetic work.</p>

        <h3>Grade R/RA</h3>
        <p>Repaired or modified vehicles. Requires careful inspection.</p>

        <h2>Interior and Exterior Grades</h2>
        <p>In addition to overall grades, auctions provide separate ratings for interior and exterior condition using letters A through E, where A is best.</p>

        <h2>Making Your Decision</h2>
        <p>For most buyers, vehicles graded 4.0 and above offer the best value and reliability. Always review the complete auction sheet which provides detailed information beyond just the grade.</p>
      `
    }
  };

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        console.log('Fetching blog post with slug:', slug);
        const response = await axios.get(`${BACKEND_URL}/api/public/blog/posts/${slug}`);
        const p = response.data;
        console.log('API response:', p);
        console.log('image_url:', p.image_url ? 'present (' + p.image_url.length + ' chars)' : 'missing');
        console.log('image:', p.image ? 'present' : 'missing');

        const createdAt = p.created_at ? new Date(p.created_at) : null;
        const date = createdAt && !Number.isNaN(createdAt.getTime())
          ? createdAt.toISOString().slice(0, 10)
          : (p.date || new Date().toISOString().slice(0, 10));

        const words = (p.content || '').toString().trim().split(/\s+/).filter(Boolean).length;
        const minutes = Math.max(1, Math.round(words / 200));

        setPost({
          ...p,
          date,
          readTime: p.readTime || `${minutes} min read`,
        });
        console.log('Post set successfully');
      } catch (error) {
        console.error('Error fetching post:', error);
        // Fallback to local static map (older demo routes)
        const fallback = fallbackPosts[slug];
        if (fallback) {
          setPost(fallback);
          } else {
          // If the route param is a slug, fetch all public posts and match by slug or id
          try {
            const listRes = await axios.get(`${BACKEND_URL}/api/public/blog/posts`);
            const match = (listRes.data || []).find((x) => 
              x.id === slug || x.slug === slug
            );
            if (match) {
              const createdAt = match.created_at ? new Date(match.created_at) : null;
              const date = createdAt && !Number.isNaN(createdAt.getTime())
                ? createdAt.toISOString().slice(0, 10)
                : (match.date || new Date().toISOString().slice(0, 10));

              const words = (match.content || '').toString().trim().split(/\s+/).filter(Boolean).length;
              const minutes = Math.max(1, Math.round(words / 200));

              setPost({
                ...match,
                date,
                readTime: match.readTime || `${minutes} min read`,
              });
            } else {
              setPost(null);
            }
          } catch (e) {
            setPost(null);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const getImageUrl = (post) => {
    console.log('getImageUrl called with:', post ? { image: !!post.image, image_url: !!post.image_url } : 'null post');
    if (!post) return 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1200';
    
    const url = post.image_url || post.image;
    console.log('Using image URL:', url ? (url.substring(0, 50) + '...') : 'default');
    if (url) return url;
    
    return 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader currency={currency} onCurrencyChange={setCurrency} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <p className="text-gray-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader currency={currency} onCurrencyChange={setCurrency} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Blog post not found</h2>
          <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
        </div>
      </div>
    );
  }

  const shareUrl = `${window.location.origin}/blog/${slug}`;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={`${post.title} | Global Trade Car Blog`}
        description={post.excerpt}
        ogImage={getImageUrl(post)}
        url={shareUrl}
      />
      <PublicHeader currency={currency} onCurrencyChange={setCurrency} />
      
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/blog')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Blog
        </Button>

        {/* Header */}
        <header className="mb-8">
          <Badge className="mb-4">{post.category}</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {post.title}
          </h1>
          <div className="flex items-center space-x-6 text-gray-600">
            <span className="flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              {post.readTime}
            </span>
          </div>
        </header>

        {/* Featured Image */}
        <div className="mb-8 rounded-xl overflow-hidden">
          <img 
            src={getImageUrl(post)} 
            alt={post.title}
            className="w-full h-96 object-cover"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1200';
            }}
          />
        </div>

        {/* Share Buttons */}
        <div className="mb-8 flex items-center space-x-4 pb-8 border-b">
          <span className="text-sm font-semibold text-gray-700 flex items-center">
            <Share2 className="h-4 w-4 mr-2" />
            Share:
          </span>
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <Facebook className="h-5 w-5 text-red-600" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <Twitter className="h-5 w-5 text-red-400" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <Linkedin className="h-5 w-5 text-red-700" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <Mail className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: post.content?.startsWith('<') ? post.content : convertToHtml(post.content) }}
        />

        {/* CTA */}
        <Card className="mt-12 bg-red-50 border-red-200">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Find Your Perfect Vehicle?
            </h3>
            <p className="text-gray-600 mb-6">
              Browse our inventory of quality Japanese vehicles
            </p>
            <Button 
              size="lg"
              onClick={() => navigate('/vehicles')}
              className="bg-red-600 hover:bg-red-700"
            >
              View Available Vehicles
            </Button>
          </CardContent>
        </Card>
      </article>

      <PublicFooter />
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default BlogPostPage;